// Profile Completion Calculator - Core Required Fields Only

/**
 * Normalize profile object to handle field name variants.
 * Ensures consistent completion calculation regardless of profile source.
 * @param {Object} profile - Raw profile object
 * @returns {Object} Normalized profile with canonical field names
 */
function normalizeProfileForCompletion(profile) {
  if (!profile) return {};
  
  return {
    // Bio variants: bio, about, about_text, description
    bio: profile.bio || profile.about || profile.about_text || profile.description || "",
    
    // Relationship style variants
    relationship_style: profile.relationship_style || profile.relationshipStyle || "",
    
    // Looking for variants
    looking_for: profile.looking_for || profile.lookingFor || profile.seeking || [],
    
    // Photo arrays - keep all variants for hasMainPhoto check
    profile_photos: profile.profile_photos || [],
    photos: profile.photos || [],
    images: profile.images || [],
    main_photo_url: profile.main_photo_url || profile.mainPhotoUrl || "",
    photo_url: profile.photo_url || profile.photoUrl || "",
    main_photo: profile.main_photo || profile.mainPhoto || "",
  };
}

/**
 * Calculate profile completion percentage based on CORE required fields.
 * Optional items (video intro, extra photos, interests) do NOT affect completion.
 * 
 * This ensures consistent completion calculation across all accounts:
 * - Dashboard, Admin accounts, Test accounts, Real users
 * 
 * @param {Object} profile - The profile object from /api/me/profile
 * @returns {number} Completion percentage (0-100)
 */
export function calculateProfileCompletion(profile) {
  if (!profile) return 0;
  
  // Normalize to handle field name variants
  const p = normalizeProfileForCompletion(profile);

  // 1. Relationship style - must be non-empty string
  const hasRelationshipStyle =
    typeof p.relationship_style === "string" &&
    p.relationship_style.trim().length > 0;

  // 2. Looking for - must have at least one selection
  const hasLookingFor =
    Array.isArray(p.looking_for) && p.looking_for.length > 0;

  // 3. Bio/About - must be non-empty string (already normalized)
  const hasBio = typeof p.bio === "string" && p.bio.trim().length > 0;

  // 4. Main photo - use normalized profile fields
  const hasMainPhoto = 
    (Array.isArray(p.profile_photos) && p.profile_photos.length > 0) ||
    (Array.isArray(p.photos) && p.photos.length > 0) ||
    (Array.isArray(p.images) && p.images.length > 0) ||
    (typeof p.main_photo_url === 'string' && p.main_photo_url.trim()) ||
    (typeof p.photo_url === 'string' && p.photo_url.trim()) ||
    (typeof p.main_photo === 'string' && p.main_photo.trim());

  const required = [hasRelationshipStyle, hasLookingFor, hasBio, hasMainPhoto];
  const done = required.filter(Boolean).length;

  return Math.round((done / required.length) * 100);
}

/**
 * Get a friendly message based on completion percentage
 * @param {number} percentage - Completion percentage
 * @returns {string} Friendly message
 */
export function getCompletionMessage(percentage) {
  if (percentage >= 100) {
    return "Profile complete";
  } else if (percentage >= 75) {
    return "Almost there! Just one more detail.";
  } else if (percentage >= 50) {
    return `${percentage}% complete - keep going!`;
  } else if (percentage >= 25) {
    return `${percentage}% complete`;
  } else {
    return "Add a few details to get better matches";
  }
}

/**
 * Get missing fields for a profile
 * @param {Object} profile - User profile object
 * @returns {Array<string>} Array of missing field labels
 */
export function getMissingProfileFields(profile) {
  if (!profile) return ['Relationship style', 'Looking for', 'Bio', 'Main photo'];
  
  const missing = [];
  
  if (!profile.relationship_style) {
    missing.push('Relationship style');
  }
  
  if (!profile.looking_for || profile.looking_for.length === 0) {
    missing.push('Who you\'re looking for');
  }
  
  if (!profile.about?.trim() && !profile.bio?.trim()) {
    missing.push('Bio');
  }
  
  // 🎯 PATCH: Check all photo field variants for missing photo detection
  const hasAnyPhoto = 
    (Array.isArray(profile.profile_photos) && profile.profile_photos.length > 0) ||
    (Array.isArray(profile.photos) && profile.photos.length > 0) ||
    (Array.isArray(profile.images) && profile.images.length > 0) ||
    (typeof profile.main_photo_url === 'string' && profile.main_photo_url.trim()) ||
    (typeof profile.photo_url === 'string' && profile.photo_url.trim()) ||
    (typeof profile.main_photo === 'string' && profile.main_photo.trim());
  
  if (!hasAnyPhoto) {
    missing.push('Main photo');
  }
  
  return missing;
}
